import * as errors from '@attain-apps/errors'
import { toLooseKey } from '@attain-apps/string-utils'

import * as common from '../../common'

/**
 * The three CHGLN53A reports a draw's balances are built from, keyed by the
 * part each plays in the outline's balance formula:
 *
 * ```ts
 * currentMonthBalance = priorMonthEnd - priorMidMonth + currentMidMonth
 * ```
 *
 * Envision downloads every report under the same name apart from its date
 * (`CHGLN53A_9.25.2026.xlsm`), so a report's part comes from its date: the
 * latest is the current mid-month, the last day of the month before it is the
 * prior month end, and the remaining one is the prior mid-month.
 */
export type ExpenseReports<T> = Readonly<Record<ExpenseReports.Role, T>>
export namespace ExpenseReports {
  export type Role = 'currentMidMonth' | 'priorMidMonth' | 'priorMonthEnd'

  /** How a CHGLN53A download is recognized among a message's uploads. */
  export const FILE_ROLE = {
    label: 'CHGLN53A report',
    words: ['chgln53a'],
  } as const

  /**
   * Assigns each CHGLN53A upload its part from the date in its name.
   *
   * Exactly three are required, because each part is a different report.
   */
  export function fileNamesByRole(
    fileNames: readonly string[],
  ): ExpenseReports<string> {
    const reports = fileNames
      .filter(fileName => toLooseKey(fileName).includes(FILE_ROLE.words[0]))
      .map(fileName => ({ date: _dateInFileName(fileName), fileName }))
    const fileNameByDate = new Map(
      reports.map(({ date, fileName }) => [date, fileName]),
    )
    if (reports.length !== 3 || fileNameByDate.has(undefined)) {
      const found = reports.map(({ fileName }) => fileName).join(', ')
      throw new errors.UserFriendlyError(
        `Expected three CHGLN53A reports named like CHGLN53A_9.25.2026, one each for Current Mid-Month, Prior Month End, and Prior Mid-Month. Found: ${
          found || 'none'
        }.`,
      )
    }

    // In date order the reports read prior mid-month, prior month end, current
    // mid-month; the date check rejects any set where that does not hold.
    const [priorMidMonth = '', priorMonthEnd = '', currentMidMonth = ''] =
      reports.map(({ date }) => date ?? '').sort()
    const dates = { currentMidMonth, priorMidMonth, priorMonthEnd }
    _assertDates(dates, 'file names')
    return _mapRoles(role => fileNameByDate.get(dates[role]) ?? '')
  }

  /**
   * Checks each report's `As of Date` fits its part, so a renamed or swapped
   * download fails with the date that is wrong instead of producing wrong
   * balances.
   */
  export function assertAsOfDates(
    sheets: ExpenseReports<readonly common.SheetRow[]>,
  ): void {
    const dates = _mapRoles(role => {
      const date = _asOfDate(sheets[role])
      if (!date) {
        throw new errors.UserFriendlyError(
          `The ${LABELS[role]} CHGLN53A report has no "As of Date" above its header.`,
        )
      }
      return date
    })
    _assertDates(dates, 'As of Dates')
  }

  const LABELS: ExpenseReports<string> = {
    currentMidMonth: 'Current Mid-Month',
    priorMidMonth: 'Prior Mid-Month',
    priorMonthEnd: 'Prior Month End',
  }

  const MONTHS = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ]

  /**
   * Checks the balance formula's date relationships: the prior month end is
   * the last day of the month before the current mid-month, and the prior
   * mid-month comes before it.
   */
  function _assertDates(dates: ExpenseReports<string>, source: string): void {
    const expectedMonthEnd = _lastDayOfPriorMonth(dates.currentMidMonth)
    if (dates.priorMonthEnd !== expectedMonthEnd) {
      throw new errors.UserFriendlyError(
        `The CHGLN53A ${source} do not fit together: Prior Month End should be ${_format(
          expectedMonthEnd,
        )}, the last day of the month before Current Mid-Month (${_format(
          dates.currentMidMonth,
        )}), but it is ${_format(dates.priorMonthEnd)}.`,
      )
    }
    if (dates.priorMidMonth >= dates.priorMonthEnd) {
      throw new errors.UserFriendlyError(
        `The CHGLN53A ${source} do not fit together: Prior Mid-Month (${_format(
          dates.priorMidMonth,
        )}) should come before Prior Month End (${_format(
          dates.priorMonthEnd,
        )}).`,
      )
    }
  }

  /** Builds a value for each report from its role. */
  function _mapRoles<T>(value: (role: Role) => T): ExpenseReports<T> {
    return {
      currentMidMonth: value('currentMidMonth'),
      priorMidMonth: value('priorMidMonth'),
      priorMonthEnd: value('priorMonthEnd'),
    }
  }

  /** The `M.D.YYYY` date in a download's name, as an ISO date. */
  function _dateInFileName(fileName: string): string | undefined {
    const match = /(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(fileName)
    return match ? _isoDate(match[3], match[1], match[2]) : undefined
  }

  /** The report's `As of Date: September 25, 2026`, as an ISO date. */
  function _asOfDate(rows: readonly common.SheetRow[]): string | undefined {
    for (const cell of rows.flat()) {
      const match = /As of Date:\s*([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})/i.exec(
        common.SheetRow.cellText(cell) ?? '',
      )
      const month = MONTHS.indexOf(match?.[1]?.toLowerCase() ?? '')
      if (match && month !== -1) {
        return _isoDate(match[3], String(month + 1), match[2])
      }
    }
    return undefined
  }

  /** Joins date parts as `YYYY-MM-DD`. */
  function _isoDate(
    year: string | undefined,
    month: string | undefined,
    day: string | undefined,
  ): string {
    return [year, month?.padStart(2, '0'), day?.padStart(2, '0')].join('-')
  }

  /** For `2026-09-25`, `2026-08-31`. */
  function _lastDayOfPriorMonth(date: string): string {
    const [year = 0, month = 1] = date.split('-').map(Number)
    // Day 0 of a month is the last day of the month before it.
    return new Date(Date.UTC(year, month - 1, 0)).toISOString().slice(0, 10)
  }

  /** Formats an ISO date as `M/D/YYYY`, the way the file names read. */
  function _format(date: string): string {
    const [year = '', month = '', day = ''] = date.split('-')
    return `${Number(month)}/${Number(day)}/${year}`
  }
}
